'use client';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../utils';
import { Transition } from '@headlessui/react';
import { TooltipPosition } from '../types/TooltipPosition';

type BaseTooltipAtrributes = Pick<React.HTMLAttributes<HTMLDivElement>, 'className' | 'id'>;

const TooltipVariants = cva(
  `!z-[1111] whitespace-normal rounded-md bg-primary-base p-sm text-xs text-foreground-white shadow text-center `,
  {
    variants: {
      size: {
        sm: 'min-w-[100px] max-w-[200px]',
        md: 'min-w-[100px] max-w-[200px]',
        lg: 'min-w-[100px] max-w-[300px]',
        xl: 'min-w-[100px] max-w-[350px]'
      }
    },
    defaultVariants: {
      size: 'md'
    }
  }
);

type TooltipVariantProps = VariantProps<typeof TooltipVariants>;

export interface TooltipProps extends BaseTooltipAtrributes, TooltipVariantProps {
  label?: string;
  text?: string;
  /** Rich tooltip body rendered below label/text. */
  content?: React.ReactNode;
  position: TooltipPosition;
  children: React.ReactNode;
  trigger?: 'hover' | 'click';
  /** Render the bubble into document.body so overflow/scroll ancestors cannot clip it. */
  portal?: boolean;
  /** Extra classes merged onto the bubble (e.g. custom bg/border/text colors). */
  bubbleClassName?: string;
  /** Extra classes merged onto the arrow (keep its bg in sync with the bubble). */
  arrowClassName?: string;
  testId?: string;
}

/** Gap between the trigger and a portaled bubble — matches the sm spacing token (8px). */
const PORTAL_GAP = 8;

function getPortalStyle(pos: TooltipPosition, rect: DOMRect | null): React.CSSProperties {
  if (!rect) return { visibility: 'hidden' };
  switch (pos) {
    case 'top left':
      return { left: rect.left, top: rect.top - PORTAL_GAP, transform: 'translateY(-100%)' };
    case 'top right':
      return {
        left: rect.right,
        top: rect.top - PORTAL_GAP,
        transform: 'translate(-100%, -100%)'
      };
    case 'bottom':
      return {
        left: rect.left + rect.width / 2,
        top: rect.bottom + PORTAL_GAP,
        transform: 'translateX(-50%)'
      };
    case 'left':
      return {
        left: rect.left - PORTAL_GAP,
        top: rect.top + rect.height / 2,
        transform: 'translate(-100%, -50%)'
      };
    case 'right':
      return {
        left: rect.right + PORTAL_GAP,
        top: rect.top + rect.height / 2,
        transform: 'translateY(-50%)'
      };
    case 'top':
    case 'top center':
    default:
      return {
        left: rect.left + rect.width / 2,
        top: rect.top - PORTAL_GAP,
        transform: 'translate(-50%, -100%)'
      };
  }
}

export const Tooltip = React.forwardRef<HTMLDivElement, TooltipProps>(
  (
    {
      label,
      text,
      content,
      position,
      trigger = 'hover',
      className,
      size,
      children,
      portal = false,
      bubbleClassName,
      arrowClassName,
      testId,
      ...props
    },
    ref
  ) => {
    const [show, setShow] = React.useState<boolean>(false);
    const [anchorRect, setAnchorRect] = React.useState<DOMRect | null>(null);
    const wrapperRef = React.useRef<HTMLDivElement | null>(null);
    const tooltipId = React.useId();

    const setRefs = (node: HTMLDivElement | null) => {
      wrapperRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    };

    const animationFrameRef = React.useRef<number | null>(null);

    // rAF-throttled: scroll fires per frame anyway, so one measurement per frame is enough.
    const updateAnchorRect = React.useCallback(() => {
      if (!wrapperRef.current || animationFrameRef.current !== null) return;
      animationFrameRef.current = window.requestAnimationFrame(() => {
        if (wrapperRef.current) {
          setAnchorRect(wrapperRef.current.getBoundingClientRect());
        }
        animationFrameRef.current = null;
      });
    }, []);

    React.useEffect(() => {
      return () => {
        if (animationFrameRef.current !== null) {
          window.cancelAnimationFrame(animationFrameRef.current);
        }
      };
    }, []);

    const handleToggle = () => {
      updateAnchorRect();
      setShow(prev => !prev);
    };
    const handleShow = () => {
      updateAnchorRect();
      setShow(true);
    };
    const handleHide = () => setShow(false);

    // A portaled bubble is fixed-positioned — keep it glued to the trigger
    // while any ancestor scrolls or the window resizes.
    React.useEffect(() => {
      if (!portal || !show) return;
      window.addEventListener('scroll', updateAnchorRect, true);
      window.addEventListener('resize', updateAnchorRect);
      return () => {
        window.removeEventListener('scroll', updateAnchorRect, true);
        window.removeEventListener('resize', updateAnchorRect);
      };
    }, [portal, show, updateAnchorRect]);

    const getPositionClasses = (pos: TooltipPosition) => {
      switch (pos) {
        case 'top':
        case 'top center':
          return 'bottom-full left-1/2 -translate-x-1/2 mb-sm';
        case 'top left':
          return 'bottom-full left-0 mb-sm';
        case 'top right':
          return 'bottom-full right-0 mb-sm';
        case 'bottom':
          return 'top-full left-1/2 -translate-x-1/2 mt-sm';
        case 'left':
          return 'right-full top-1/2 -translate-y-1/2 mr-sm';
        case 'right':
          return 'left-full top-1/2 -translate-y-1/2 ml-sm';
        default:
          return 'bottom-full left-1/2 -translate-x-1/2 mb-sm';
      }
    };

    const getArrowPositionClasses = (pos: TooltipPosition) => {
      switch (pos) {
        case 'top':
        case 'top center':
          return 'top-full left-1/2 -translate-x-1/2 mt-[-5px]';
        case 'top left':
          return 'top-full left-4 mt-[-5px]';
        case 'top right':
          return 'top-full right-4 mt-[-5px]';
        case 'bottom':
          return 'bottom-full left-1/2 -translate-x-1/2 mb-[-5px]';
        case 'left':
          return 'left-full top-1/2 -translate-y-1/2 ml-[-5px]';
        case 'right':
          return 'right-full top-1/2 -translate-y-1/2 mr-[-5px]';
        default:
          return 'top-full left-1/2 -translate-x-1/2 mt-[-5px]';
      }
    };

    const bubble = (
      <Transition
        show={show}
        enter='transition-opacity duration-300'
        enterFrom='opacity-0'
        enterTo='opacity-100'
        leave='transition-opacity duration-300'
        leaveFrom='opacity-100'
        leaveTo='opacity-0'
      >
        <div
          id={tooltipId}
          role='tooltip'
          className={cn(
            TooltipVariants({ size }),
            portal ? 'fixed' : cn('absolute', getPositionClasses(position)),
            bubbleClassName
          )}
          style={portal ? getPortalStyle(position, anchorRect) : undefined}
        >
          <div className='flex flex-col items-center justify-center'>
            {label && <h2 className='font-semibold text-center'>{label}</h2>}
            {text && <div className='mt-xs text-xs2 text-center'>{text}</div>}
            {content}
          </div>
          <div
            className={cn(
              'absolute w-2 h-2 bg-primary-base rotate-45',
              getArrowPositionClasses(position),
              arrowClassName
            )}
          />
        </div>
      </Transition>
    );

    return (
      <div
        ref={setRefs}
        className={cn('relative inline-block', className)}
        onMouseEnter={trigger === 'hover' ? handleShow : undefined}
        onMouseLeave={trigger === 'hover' ? handleHide : undefined}
        onFocus={trigger === 'hover' ? handleShow : undefined}
        onBlur={trigger === 'hover' ? handleHide : undefined}
        onClick={trigger === 'click' ? handleToggle : undefined}
        aria-describedby={show ? tooltipId : undefined}
        data-testid={testId}
        tabIndex={0}
        {...props}
      >
        {children}

        {portal && typeof document !== 'undefined' ? createPortal(bubble, document.body) : bubble}
      </div>
    );
  }
);
Tooltip.displayName = 'Tooltip';
